// Non-component helpers for the structured rule editor fields — split out of
// RuleEditorParts.tsx (which exports only components + string constants) so
// react-refresh's only-export-components rule doesn't flag these functions.
import { type RulePackageRule, type UpdateRuleInput } from "@api/rest/types-and-hooks";
import { ReportType } from "@pages/Templates/constants";

// The IA / RA chips on a rule. They filter which reports the rule is sent for.
export const RuleChip = {
  IA: "IA",
  RA: "RA",
} as const;
export type RuleChip = (typeof RuleChip)[keyof typeof RuleChip];

export const RULE_CHIPS: RuleChip[] = [RuleChip.IA, RuleChip.RA];

const CHIP_REPORT_TYPE: Record<RuleChip, string> = {
  [RuleChip.IA]: ReportType.INITIAL_ASSESSMENT,
  [RuleChip.RA]: ReportType.REASSESSMENT,
};

/** A rule's `report_types` as chips. Empty (= every report type) shows both on. */
export const chipsFor = (reportTypes: string[]): RuleChip[] =>
  reportTypes.length === 0 ? [...RULE_CHIPS] : RULE_CHIPS.filter((c) => reportTypes.includes(CHIP_REPORT_TYPE[c]));

/** Both chips on saves as empty (= every report type), so the rule isn't narrowed out of others. */
export const reportTypesForChips = (chips: RuleChip[]): string[] =>
  chips.length === RULE_CHIPS.length ? [] : chips.map((c) => CHIP_REPORT_TYPE[c]);

// Toggle `chip` in `current`, enforcing at least one selected. Returns the same
// array reference when the toggle would drop the last one, so callers can detect a no-op.
export const toggleChip = (current: RuleChip[], chip: RuleChip): RuleChip[] => {
  const has = current.includes(chip);
  if (has && current.length === 1) return current;
  return RULE_CHIPS.filter((c) => (c === chip ? !has : current.includes(c)));
};

/** A rule's editable fields, as the editor holds them. */
export interface PackageRuleFields {
  name: string;
  category: string;
  chips: RuleChip[];
  location: string;
  scanFor: string;
  feedback: string;
}

export const fieldsOf = (rule: RulePackageRule): PackageRuleFields => ({
  name: rule.name,
  category: rule.category,
  chips: chipsFor(rule.report_types),
  location: rule.location,
  scanFor: rule.scan_for,
  feedback: rule.feedback,
});

type FieldKey = keyof PackageRuleFields;
const FIELD_KEYS: FieldKey[] = ["name", "category", "chips", "location", "scanFor", "feedback"];

const sameValue = <K extends FieldKey>(a: PackageRuleFields[K], b: PackageRuleFields[K]): boolean =>
  Array.isArray(a) && Array.isArray(b) ? a.join() === b.join() : a === b;

/** The fields in `fields` that differ from `base` (the saved rule). */
export const changedFields = (fields: PackageRuleFields, base: PackageRuleFields): Partial<PackageRuleFields> =>
  Object.fromEntries(FIELD_KEYS.filter((k) => !sameValue(fields[k], base[k])).map((k) => [k, fields[k]]));

/** `draft` without the fields that still hold what was just saved; edits made while the save was in flight stay. */
export const draftAfterSave = (
  draft: Partial<PackageRuleFields>,
  saved: Partial<PackageRuleFields>,
): Partial<PackageRuleFields> =>
  Object.fromEntries(
    FIELD_KEYS.filter((k) => k in draft && !(k in saved && sameValue(draft[k]!, saved[k]!))).map((k) => [k, draft[k]]),
  );

/** Only the given fields, as the update API takes them — saved directly, no formatting. */
export const ruleInputFrom = (fields: Partial<PackageRuleFields>): Omit<UpdateRuleInput, "id"> => ({
  ...(fields.name !== undefined && { name: fields.name }),
  ...(fields.category !== undefined && { category: fields.category }),
  ...(fields.chips !== undefined && { report_types: reportTypesForChips(fields.chips) }),
  ...(fields.location !== undefined && { location: fields.location }),
  ...(fields.scanFor !== undefined && { scan_for: fields.scanFor }),
  ...(fields.feedback !== undefined && { feedback: fields.feedback }),
});
